// What the company's side is doing right now — activity, who is answering, the
// active cart. Deliberately in memory only: it has no sequence, never enters
// history, and a status restored from disk after a restart would be a lie about
// the present. The activity indicator lapses on its own at `expires`, so a
// server that died mid-task leaves nothing hanging.

import type { ThreadActivity, ThreadState } from '$lib/protocol/types';

export interface LiveThreadState {
  activity: ThreadActivity | undefined;
  answeredBy: string | undefined;
  activeCart: string | undefined;
  readOnly: boolean;
}

class ThreadStateStore {
  states = $state<Record<string, LiveThreadState>>({});

  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  apply(accountId: string, incoming: ThreadState[]): void {
    const first = incoming[0];
    if (first === undefined) {
      // No state object on this page means no information — not a clear signal.
      // Removal of an indicator arrives as a state WITHOUT the activity field.
      return;
    }
    this.clearTimer(accountId);

    let activity = first.activity;
    if (activity !== undefined) {
      const remaining = activity.expires.getTime() - Date.now();
      if (remaining <= 0) {
        activity = undefined;
      } else {
        this.timers.set(
          accountId,
          setTimeout(() => this.expireActivity(accountId), remaining + 50),
        );
      }
    }

    this.states = {
      ...this.states,
      [accountId]: {
        activity,
        answeredBy: first.answeredBy,
        activeCart: first.activeCart,
        readOnly: first.readOnly,
      },
    };
  }

  get(accountId: string): LiveThreadState | undefined {
    return this.states[accountId];
  }

  clear(accountId: string): void {
    this.clearTimer(accountId);
    const { [accountId]: _, ...rest } = this.states;
    this.states = rest;
  }

  private expireActivity(accountId: string): void {
    const current = this.states[accountId];
    if (current === undefined || current.activity === undefined) {
      return;
    }
    this.states = { ...this.states, [accountId]: { ...current, activity: undefined } };
  }

  private clearTimer(accountId: string): void {
    const timer = this.timers.get(accountId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(accountId);
    }
  }
}

export const threadState = new ThreadStateStore();
